/**
 * Security Monitor
 *
 * Advanced security monitoring system that tracks authentication events,
 * detects suspicious patterns, and provides real-time threat assessment.
 *
 * Features:
 * - Multi-device login detection
 * - Geographic anomaly detection
 * - Suspicious activity pattern recognition
 * - Failed attempt tracking and rate limiting
 * - Security score calculation
 * - Real-time threat notifications
 *
 * Created: September 22, 2025
 * Part of: Enterprise JWT Implementation Phase 3
 */

import productionLogger from './monitoringFix';

class SecurityMonitor {
  constructor() {
    // Security event storage
    this.events = {
      logins: [],
      logouts: [],
      failures: [],
      suspicious: [],
      deviceChanges: [],
      locationChanges: []
    };

    // Security thresholds
    this.thresholds = {
      maxFailedAttempts: 5,        // Max failed attempts before lockout
      failureWindowMs: 300000,      // 5 minutes
      multiDeviceAlertCount: 3,     // Alert after 3 devices
      suspiciousPatternScore: 0.7,  // Score threshold for suspicious activity
      geoAnomalyDistance: 500       // km - physical impossibility threshold
    };

    // Active sessions tracking
    this.activeSessions = new Map(); // sessionId -> session info
    this.deviceFingerprints = new Set();
    this.knownLocations = [];

    // Security score (0-100, higher is better)
    this.securityScore = 100;

    // Initialize browser fingerprinting
    this.initializeFingerprinting();
  }

  /**
   * Initialize browser fingerprinting
   */
  initializeFingerprinting() {
    if (typeof window !== 'undefined') {
      this.currentFingerprint = this.generateDeviceFingerprint();
    }
  }

  /**
   * Generate device fingerprint
   */
  generateDeviceFingerprint() {
    if (typeof window === 'undefined') return 'unknown';

    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      colorDepth: window.screen.colorDepth,
      hardwareConcurrency: navigator.hardwareConcurrency,
      webglVendor: this.getWebGLVendor(),
      canvasFingerprint: this.getCanvasFingerprint()
    };

    // Create hash of fingerprint
    return this.hashFingerprint(fingerprint);
  }

  /**
   * Get WebGL vendor information
   */
  getWebGLVendor() {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return 'unknown';

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (!debugInfo) return 'unknown';

      return gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    } catch (e) {
      return 'unknown';
    }
  }

  /**
   * Generate canvas fingerprint
   */
  getCanvasFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('Canvas fingerprint', 2, 15);
      return canvas.toDataURL().substring(0, 100);
    } catch (e) {
      return 'unknown';
    }
  }

  /**
   * Hash fingerprint data
   */
  hashFingerprint(data) {
    const str = JSON.stringify(data);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(16);
  }

  /**
   * Track login event
   */
  trackLogin(userId, metadata = {}) {
    const event = {
      type: 'login',
      userId,
      timestamp: Date.now(),
      fingerprint: this.currentFingerprint,
      ...metadata,
      location: this.getCurrentLocation(),
      sessionId: this.generateSessionId()
    };

    this.events.logins.push(event);
    this.activeSessions.set(event.sessionId, event);

    // Check for multi-device login
    const recentLogins = this.getRecentLogins(userId, 86400000); // 24 hours
    const uniqueDevices = new Set(recentLogins.map(l => l.fingerprint));

    if (uniqueDevices.size >= this.thresholds.multiDeviceAlertCount) {
      this.raiseSuspiciousActivity('multi_device_login', {
        userId,
        deviceCount: uniqueDevices.size,
        devices: Array.from(uniqueDevices)
      });
    }

    // Check for geographic anomaly
    if (metadata.location) {
      this.checkGeographicAnomaly(userId, metadata.location);
    }

    // Update security score
    this.updateSecurityScore();

    // Log security event
    productionLogger.logSecurityEvent('login_tracked', event);

    return event.sessionId;
  }

  /**
   * Track logout event
   */
  trackLogout(sessionId) {
    const event = {
      type: 'logout',
      sessionId,
      timestamp: Date.now()
    };

    this.events.logouts.push(event);
    this.activeSessions.delete(sessionId);

    // Log security event
    productionLogger.logSecurityEvent('logout_tracked', event);
  }

  /**
   * Track failed login attempt
   */
  trackFailedLogin(email, reason = 'invalid_credentials') {
    const event = {
      type: 'login_failure',
      email,
      reason,
      timestamp: Date.now(),
      fingerprint: this.currentFingerprint,
      location: this.getCurrentLocation()
    };

    this.events.failures.push(event);

    // Check for brute force attack
    const recentFailures = this.getRecentFailures(email);
    if (recentFailures.length >= this.thresholds.maxFailedAttempts) {
      this.raiseSuspiciousActivity('brute_force_attempt', {
        email,
        attempts: recentFailures.length,
        timeWindow: this.thresholds.failureWindowMs
      });

      // Trigger account lockout
      return {
        shouldLockout: true,
        attempts: recentFailures.length,
        nextAllowedAttempt: Date.now() + this.thresholds.failureWindowMs
      };
    }

    // Update security score
    this.updateSecurityScore();

    // Log security event
    productionLogger.logSecurityEvent('failed_login_tracked', event);

    return {
      shouldLockout: false,
      attempts: recentFailures.length,
      remainingAttempts: this.thresholds.maxFailedAttempts - recentFailures.length
    };
  }

  /**
   * Get recent login events for user
   */
  getRecentLogins(userId, windowMs = 3600000) {
    const cutoff = Date.now() - windowMs;
    return this.events.logins.filter(
      e => e.userId === userId && e.timestamp > cutoff
    );
  }

  /**
   * Get recent failed login attempts
   */
  getRecentFailures(email) {
    const cutoff = Date.now() - this.thresholds.failureWindowMs;
    return this.events.failures.filter(
      e => e.email === email && e.timestamp > cutoff
    );
  }

  /**
   * Check for geographic anomaly
   */
  async checkGeographicAnomaly(userId, currentLocation) {
    const recentLogins = this.getRecentLogins(userId, 3600000); // 1 hour

    if (recentLogins.length === 0) return;

    const lastLogin = recentLogins[recentLogins.length - 1];
    if (!lastLogin.location) return;

    const distance = this.calculateDistance(
      lastLogin.location,
      currentLocation
    );

    const timeDiff = Date.now() - lastLogin.timestamp;
    const speedKmh = (distance / (timeDiff / 3600000));

    // Check for physical impossibility (>500 km/h)
    if (speedKmh > this.thresholds.geoAnomalyDistance) {
      this.raiseSuspiciousActivity('geographic_anomaly', {
        userId,
        distance,
        timeDiff,
        speedKmh,
        lastLocation: lastLogin.location,
        currentLocation
      });
    }
  }

  /**
   * Calculate distance between two coordinates (Haversine formula)
   */
  calculateDistance(loc1, loc2) {
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(loc2.lat - loc1.lat);
    const dLon = this.toRad(loc2.lon - loc1.lon);

    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.toRad(loc1.lat)) * Math.cos(this.toRad(loc2.lat)) *
      Math.sin(dLon/2) * Math.sin(dLon/2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   */
  toRad(degrees) {
    return degrees * (Math.PI / 180);
  }

  /**
   * Raise suspicious activity alert
   */
  raiseSuspiciousActivity(type, details) {
    const alert = {
      type,
      details,
      timestamp: Date.now(),
      severity: this.calculateSeverity(type),
      fingerprint: this.currentFingerprint
    };

    this.events.suspicious.push(alert);

    // Log critical security event
    productionLogger.logSecurityEvent('suspicious_activity_detected', alert);

    // Notify listeners (for UI notifications)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('securityAlert', { detail: alert }));
    }

    // Update security score
    this.securityScore = Math.max(0, this.securityScore - (alert.severity * 10));

    return alert;
  }

  /**
   * Calculate alert severity
   */
  calculateSeverity(type) {
    const severityMap = {
      brute_force_attempt: 0.9,
      geographic_anomaly: 0.8,
      multi_device_login: 0.5,
      suspicious_pattern: 0.7,
      token_tampering: 1.0
    };
    return severityMap[type] || 0.5;
  }

  /**
   * Get current location (placeholder - would use geolocation API)
   */
  getCurrentLocation() {
    // In production, this would use the Geolocation API
    // For now, return a placeholder
    return {
      lat: 0,
      lon: 0,
      accuracy: 'unknown'
    };
  }

  /**
   * Generate session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  }

  /**
   * Update security score based on recent events
   */
  updateSecurityScore() {
    let score = 100;

    // Deduct for recent failures
    const recentFailures = this.events.failures.filter(
      e => e.timestamp > Date.now() - 3600000
    ).length;
    score -= recentFailures * 5;

    // Deduct for suspicious activities
    const recentSuspicious = this.events.suspicious.filter(
      e => e.timestamp > Date.now() - 86400000
    ).length;
    score -= recentSuspicious * 10;

    // Bonus for consistent device usage
    if (this.deviceFingerprints.size === 1) {
      score += 10;
    }

    // Ensure score is between 0 and 100
    this.securityScore = Math.max(0, Math.min(100, score));

    return this.securityScore;
  }

  /**
   * Get security report
   */
  getSecurityReport() {
    return {
      score: this.securityScore,
      activeSessions: this.activeSessions.size,
      recentFailures: this.getRecentFailures('').length,
      suspiciousActivities: this.events.suspicious.filter(
        e => e.timestamp > Date.now() - 86400000
      ),
      deviceCount: this.deviceFingerprints.size,
      recommendations: this.getSecurityRecommendations()
    };
  }

  /**
   * Get security recommendations
   */
  getSecurityRecommendations() {
    const recommendations = [];

    if (this.securityScore < 70) {
      recommendations.push('Enable two-factor authentication for enhanced security');
    }

    if (this.deviceFingerprints.size > 3) {
      recommendations.push('Review and remove unused devices from your account');
    }

    if (this.events.failures.length > 10) {
      recommendations.push('Consider using a password manager for stronger passwords');
    }

    if (this.events.suspicious.length > 0) {
      recommendations.push('Review recent security alerts and verify all activities');
    }

    return recommendations;
  }

  /**
   * Clear old events (data retention policy)
   */
  cleanupOldEvents(retentionDays = 30) {
    const cutoff = Date.now() - (retentionDays * 86400000);

    // Clean up each event type
    for (const eventType of Object.keys(this.events)) {
      this.events[eventType] = this.events[eventType].filter(
        e => e.timestamp > cutoff
      );
    }
  }

  /**
   * Export security data for analysis
   */
  exportSecurityData() {
    return {
      timestamp: Date.now(),
      score: this.securityScore,
      events: this.events,
      activeSessions: Array.from(this.activeSessions.entries()),
      deviceFingerprints: Array.from(this.deviceFingerprints),
      report: this.getSecurityReport()
    };
  }
}

// Create singleton instance
const securityMonitor = new SecurityMonitor();

// Export for use in other modules
export default securityMonitor;